import { useCallback, useEffect, useRef } from 'react'

/**
 * Returns a debounced version of `callback` that only fires `delayMs` after
 * the last call. The pending call is flushed immediately via the returned
 * `flush()` and cancelled on unmount so no stale commit fires after the
 * component using it goes away.
 */
export function useDebouncedCallback<Args extends unknown[]>(
  callback: (...args: Args) => void,
  delayMs: number
): {
  debounced: (...args: Args) => void
  flush: () => void
  cancel: () => void
} {
  const callbackRef = useRef(callback)
  callbackRef.current = callback

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingArgsRef = useRef<Args | null>(null)

  const cancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    pendingArgsRef.current = null
  }, [])

  const flush = useCallback(() => {
    if (timeoutRef.current && pendingArgsRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
      const args = pendingArgsRef.current
      pendingArgsRef.current = null
      callbackRef.current(...args)
    }
  }, [])

  const debounced = useCallback(
    (...args: Args) => {
      pendingArgsRef.current = args
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null
        const pending = pendingArgsRef.current
        pendingArgsRef.current = null
        if (pending) callbackRef.current(...pending)
      }, delayMs)
    },
    [delayMs]
  )

  useEffect(() => cancel, [cancel])

  return { debounced, flush, cancel }
}
